"use client";

import React, { useEffect, useRef, useState } from "react";
import Image from "next/image";
import purpleArrowLeft from "@/app/assets/purple-arrow-down-left.svg";
import purpleArrowRight from "@/app/assets/purple-arrow-down-right.svg";
import gsap, { Power4 } from "gsap";
import ScrollTrigger from "gsap/dist/ScrollTrigger";
import { useGSAP } from "@gsap/react";
import { usePathname } from "next/navigation";

import EffectiveTextIcon from "@/app/assets/effective-circular-icon.svg";
import ResourceTextIcon from "@/app/assets/resource-circular-icon.svg";
import SocialIcon from "@/app/assets/social-icon.svg";
import VideoTextIcon from "@/app/assets/video-circular-icon.svg";
import DigitalExperienceIcon from "@/app/assets/digital-experience-icon.svg";
import OmnichannelIcon from "@/app/assets/omnichannel-icon.svg";
import MediaIcon from "@/app/assets/media-icon.svg";
import IntegratedMarketingIcon from "@/app/assets/integrated-marketing-icon.svg";
import NewsIcon from "@/app/assets/news-circular.svg";
import CareerIcon from "@/app/assets/career-circular.svg";
import ExportiseIcon from "@/app/assets/expertise-circular.svg";
import ContactIcon from "@/app/assets/contact-circular.svg";
import HumanisingIcon from "@/app/assets/humanising-circular.svg";
import MadeDifferentlyIcon from "@/app/assets/made-differently.png";
import DNAIcon from "@/app/assets/our-dna-circular.svg";
import AwardsIcon from "@/app/assets/awards-circular.svg";
import ClientsIcon from "@/app/assets/clients-circular.svg";
import ScrollIcon from "@/app/assets/scroll-icon.svg";
import ScrollOnIcon from "@/app/assets/scroll-on-icon.svg";
import OurDnaIcon from "@/app/assets/our-dna-icon.svg";
import AnnouncementIcon from "@/app/assets/announcement-circular.svg";
import TiktokIcon from "@/app/assets/tiktok-icon.svg";
import IntroductionIcon from "@/app/assets/introduction-circular-icon.svg";
import WeAreKdIcon from "@/app/assets/we-are-kd-circular.svg";

gsap.registerPlugin(ScrollTrigger);

type ArrowProps = {
    direction: string;
    className: React.ReactNode;
    extraMargin?: boolean;
    name?: string;
    arrowtype?: string;
};

export default function BannerFloatingArrow({
    direction,
    extraMargin,
    className,
    name,
    arrowtype,
}: ArrowProps) {
    const arrowWrapRef = useRef(null);
    const arrowRef = useRef(null);
    const currentPath =
        usePathname().split("/")[usePathname().split("/").length - 1];

    const [rotateImage, setRotateImage] = useState(MadeDifferentlyIcon);

    useEffect(() => {
        if (name) {
            switch (name) {
                case "name":
                    setRotateImage(NewsIcon);
                    break;
                case "dna":
                    setRotateImage(DNAIcon);
                    break;
                case "awards":
                    setRotateImage(AwardsIcon);
                    break;
                case "clients":
                    setRotateImage(ClientsIcon);
                    break;
                case "news":
                    setRotateImage(NewsIcon);
                    break;
                case "careers":
                    setRotateImage(CareerIcon);
                    break;
                case "our-dna":
                    setRotateImage(OurDnaIcon);
                    break;
                case "announcements":
                    setRotateImage(AnnouncementIcon);
                    break;
                case "we-are-kd":
                    setRotateImage(WeAreKdIcon);
                    break;
            }
            return;
        }
        switch (currentPath) {
            case "integrated-marketing":
                setRotateImage(IntegratedMarketingIcon);
                break;
            case "social-and-content-marketing":
                setRotateImage(SocialIcon);
                break;
            case "video-production":
                setRotateImage(VideoTextIcon);
                break;
            case "omnichannel-creatives":
                setRotateImage(OmnichannelIcon);
                break;
            case "media-strategy":
                setRotateImage(MediaIcon);
                break;
            case "digital-experiences":
                setRotateImage(DigitalExperienceIcon);
                break;
            case "news":
                setRotateImage(NewsIcon);
                break;
            case "careers":
                setRotateImage(CareerIcon);
                break;
            case "services":
                setRotateImage(ExportiseIcon);
                break;
            case "contact-us":
                setRotateImage(ContactIcon);
                break;
            case "about":
                setRotateImage(MadeDifferentlyIcon);
                break;
            case "tik-tok-engagement":
                setRotateImage(TiktokIcon);
                break;
            case "resources":
                setRotateImage(ResourceTextIcon);
                break;
            case "customer-data-platform":
                setRotateImage(IntroductionIcon);
                break;
            default:
                break;
        }

        switch (arrowtype) {
            case "scroll":
                setRotateImage(ScrollIcon);
                break;
            case "scroll-on":
                setRotateImage(ScrollOnIcon);
            default:
                break;
        }
    }, [currentPath, arrowtype, name]);

    useGSAP(() => {
        const tl = gsap.timeline({
            scrollTrigger: {
                trigger: arrowRef.current,
                start: "top 75%",
                end: "+=450",
                toggleActions: "play reverse play reverse",
                scrub: 1.05,
            },
        });

        if (direction === "left" || direction === "right") {
            tl.to(arrowRef.current, {
                rotate: 0,
                transformOrigin: "center",
            });
        }

        const magnets = document.querySelectorAll(".magnetic");
        const strength = 80;

        magnets.forEach((magnet) => {
            magnet.addEventListener("mousemove", moveMagnet);
            magnet.addEventListener("mouseout", (event) => {
                gsap.to(event.currentTarget, 1, {
                    x: 0,
                    y: 0,
                    ease: Power4.easeOut,
                });
            });
        });

        function moveMagnet(event: any) {
            const magnetButton = event.currentTarget;
            const bounding = magnetButton.getBoundingClientRect();

            gsap.to(magnetButton, 1, {
                x:
                    ((event.clientX - bounding.left) /
                        magnetButton.offsetWidth -
                        0.5) *
                    strength,
                y:
                    ((event.clientY - bounding.top) /
                        magnetButton.offsetHeight -
                        0.5) *
                    strength,
                ease: Power4.easeOut,
            });
        }
    });

    const arrowImg = direction === "left" ? purpleArrowLeft : purpleArrowRight;

    return (
        <div
            className={`absolute duration-75 z-10 ${className}`}
            ref={arrowWrapRef}
        >
            <div className="relative bg-white rounded-full flex w-[8.333vw] min-w-24 aspect-square justify-center items-center">
                <Image
                    src={rotateImage}
                    width={260}
                    height={260}
                    sizes="(min-width: 1024px) 8.333vw, 96px"
                    alt="rotating img"
                    className="absolute top-0 bottom-0 left-0 right-0 m-auto w-full h-full object-contain p-[5%] animate-rotate origin-center"
                    loading="lazy"
                />
                <Image
                    src={arrowImg}
                    width={120}
                    height={120}
                    sizes="(min-width: 1024px) 3.333vw, 38px"
                    alt="arrow img"
                    className={`w-[40%] m-auto h-auto aspect-square object-contain origin-center ${
                        direction === "left" ? "-rotate-90" : "rotate-90"
                    }`}
                    ref={arrowRef}
                    loading="lazy"
                />
            </div>
        </div>
    );
}
